/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import moment from 'moment';
import { cloneDeep } from 'lodash';
import Store from '../store/store';
import { updateCadence } from '../actions/cadenceActions';
import { openModal } from '../actions/modalActions';
import modalTypes from '../constants/modalTypes';
import {
  WorkPackageStatuses,
  CadenceStatuses
} from '../constants/storyStatusTypes';
import { formatDateHyphen } from './dateHelpers';

const plannedSprints = () => {
  const sprintList = Store.getState().cadenceReducer.cadences;
  const selectedTeamId = Store.getState().workPackageReducer.selectedSpace.id;

  return sprintList.filter(
    (item) =>
      item.status === CadenceStatuses.GETTING_READY &&
      !item.deleted_at &&
      item.team_id === selectedTeamId
  );
};

export const validEndDate = (sprint) => {
  const sprintEnd = new Date(sprint.end_date);
  const planned = plannedSprints() || [];
  const currentSprintIndex = planned.findIndex(
    (plannedSprint) => sprint.id === plannedSprint.id
  );
  const nextSprint =
    planned.length > currentSprintIndex + 1
      ? planned[currentSprintIndex + 1]
      : null;
  const nextSprintStart = nextSprint && new Date(nextSprint.start_date);
  if (nextSprint && sprintEnd <= nextSprintStart) return true;
  if (!nextSprint) return true;
  return false;
};

const shiftOneSprint = (
  currentSprint,
  nextSprint,
  status,
  sprintEnd,
  unfinishedWorkPackages,
  closeModal
) => {
  const nextStartDate = moment(sprintEnd);
  if (nextStartDate.isBefore(nextSprint.end_date)) {
    Store.dispatch(
      updateCadence(nextSprint.id, { cadence: { start_date: sprintEnd } })
    ).then(() => {
      Store.dispatch(
        updateCadence(currentSprint.id, {
          status,
          cadence: { end_date: sprintEnd },
          unfinished_work_packages_ids: unfinishedWorkPackages
        })
      );
      if (closeModal) closeModal();
    });
  } else
    Store.dispatch(
      updateCadence(nextSprint.id, {
        cadence: {
          start_date: sprintEnd,
          end_date: moment(sprintEnd).add(currentSprint.interval, 'days')
        }
      })
    ).then(() => {
      Store.dispatch(
        updateCadence(currentSprint.id, {
          status,
          cadence: { end_date: sprintEnd },
          unfinished_work_packages_ids: unfinishedWorkPackages
        })
      );
      if (closeModal) closeModal();
    });
};

const shiftAllSprints = (
  sprint,
  status,
  sprintEnd,
  unfinishedWorkPackages,
  closeModal
) => {
  const planned = plannedSprints().filter(
    (plannedSprint) => plannedSprint.id > sprint.id
  );
  const currentSprintIndex = planned.findIndex(
    (plannedSprint) => sprint.id === plannedSprint.id
  );
  const nextSprint = planned[currentSprintIndex + 1];
  const interval = nextSprint
    ? moment(sprintEnd).diff(nextSprint.start_date, 'days')
    : null;
  const updatePlanned = Promise.all(
    [...planned].reverse().map((item) => {
      const startDate = moment(item.start_date).add(interval, 'days');
      const endDate = moment(item.end_date).add(interval, 'days');
      const sprintInfo = {
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      };
      return new Promise((resolve) => {
        Store.dispatch(
          updateCadence(item.id, {
            cadence: sprintInfo
          })
        );
        resolve(item);
      });
    })
  );

  updatePlanned.then(() => {
    Store.dispatch(
      updateCadence(sprint.id, {
        status,
        cadence: { end_date: sprintEnd },
        unfinished_work_packages_ids: unfinishedWorkPackages
      })
    );
    if (closeModal) closeModal();
  });
};

export const adjustDates = (
  currentSprint,
  status,
  newEndDate = moment().format('YYYY-MM-DD'),
  unfinishedWorkPackages = [],
  closeModal
) => {
  const planned = plannedSprints() || [];
  const currentSprintIndex = planned.findIndex(
    (plannedSprint) => currentSprint.id === plannedSprint.id
  );
  const nextSprint =
    planned.length > currentSprintIndex + 1
      ? planned[currentSprintIndex + 1]
      : null;
  if (nextSprint) {
    const endStatus = status || currentSprint.status;
    const endDate =
      endStatus === CadenceStatuses.ACCEPTED
        ? moment().format('YYYY-MM-DD')
        : newEndDate;
    // If the next sprint starts before the new end date, we have overlap
    if (moment(nextSprint.start_date).isBefore(endDate)) {
      let message = `The start date for this sprint is after the start of ${nextSprint.short_name}.`;
      const actions = {};
      if (moment(endDate).isBefore(nextSprint.end_date))
        actions.changeOne = {
          name: `Change the start date of ${nextSprint.short_name}`,
          function: () =>
            shiftOneSprint(
              currentSprint,
              nextSprint,
              endStatus,
              endDate,
              unfinishedWorkPackages,
              closeModal
            )
        };
      else if (moment(endDate).isAfter(nextSprint.end_date)) {
        message = `The start date of this sprint is after the start and end of ${nextSprint.short_name}`;
        if (planned.length - currentSprintIndex - 1 === 1)
          actions.changeOne = {
            name: `Change the start and end dates of ${nextSprint.short_name}`,
            function: () =>
              shiftOneSprint(
                currentSprint,
                nextSprint,
                endStatus,
                endDate,
                unfinishedWorkPackages,
                closeModal
              )
          };
      }

      if (planned.length - currentSprintIndex - 1 > 1) {
        actions.changeAll = {
          name: 'Change the dates of all planned sprints',
          function: () =>
            shiftAllSprints(
              currentSprint,
              endStatus,
              endDate,
              unfinishedWorkPackages,
              closeModal
            )
        };
      }
      Store.dispatch(openModal(modalTypes.message, { message, actions }));
    }
  }
};

export const openEditSprint = (sprint) => {
  setTimeout(
    () =>
      Store.dispatch(
        openModal(modalTypes.cadenceDetail, {
          id: sprint.id,
          cadence: sprint
        })
      ),
    500
  );
};

const focusEndDate = () => {
  const field = document.querySelector('.end-date');
  field.focus();
};

export const startSprint = (currentSprint) => {
  const sprint = cloneDeep(currentSprint);
  const selectedTeamId = Store.getState().workPackageReducer.selectedSpace.id;
  const sprints = Store.getState().cadenceReducer.cadences.filter(
    (item) => !item.deleted_at && item.team_id === selectedTeamId
  );

  const currentSprintIndex = sprints.findIndex((s) => s.id === sprint.id);
  const planned = plannedSprints();
  const today = moment().format('YYYY-MM-DD');

  if (today !== sprint.start_date) {
    if (sprint.end_date < sprint.start_date) {
      sprint.end_date = moment(today).add(sprint.interval || 0, 'days');
    }
    sprint.status = CadenceStatuses.IN_PROGRESS;
    const endDate = moment(sprint.end_date).format('YYYY-MM-DD');
    const startDateForMessage = moment(sprint.start_date).format('MM/DD/YY');
    const startDateForReducer = moment(sprint.start_date).format('YYYY-MM-DD');
    const message = `The start date for this sprint is currently ${startDateForMessage}. Do you want to change it to today?`;
    const actions = {
      Yes: {
        name: 'Yes',
        function: () => {
          Store.dispatch(
            updateCadence(sprint.id, {
              status: CadenceStatuses.IN_PROGRESS,
              cadence: {
                start_date: today,
                end_date: endDate,
                status: CadenceStatuses.IN_PROGRESS
              }
            })
          );
        }
      },
      No: {
        name: 'No',
        function: () => {
          Store.dispatch(
            updateCadence(sprint.id, {
              status: CadenceStatuses.IN_PROGRESS,
              cadence: {
                start_date: startDateForReducer,
                end_date: endDate,
                status: CadenceStatuses.IN_PROGRESS
              }
            })
          );
        }
      }
    };
    Store.dispatch(
      openModal(modalTypes.message, {
        message,
        actions,
        id: 'message'
      })
    );
  } else if (
    planned[currentSprintIndex + 1] &&
    planned[currentSprintIndex + 1].start_date < sprint.start_date
  ) {
    shiftAllSprints(sprint, CadenceStatuses.IN_PROGRESS, sprint.end_date);
  } else {
    Store.dispatch(
      updateCadence(sprint.id, {
        status: CadenceStatuses.IN_PROGRESS,
        cadence: {
          start_date: sprint.start_date,
          status: CadenceStatuses.IN_PROGRESS
        }
      })
    );
  }
};

export const planSprint = (sprint) => {
  Store.dispatch(
    updateCadence(sprint.id, {
      status: CadenceStatuses.GETTING_READY,
      cadence: { start_date: sprint.start_date }
    })
  );
};

export const openDetailCard = (result) => {
  switch (result.level) {
    case 1:
      return Store.dispatch(
        openModal(modalTypes.resultDetail, {
          id: result.id,
          workPackage: result
        })
      );
    case 2:
    case 3:
      return Store.dispatch(
        openModal(modalTypes.featureDetail, {
          id: result.id
        })
      );
    default:
      return null;
  }
};

// Ending sprint
// Several steps to this
// 1 first we validate the end date, if its ending early or late, handle that.
// 2 then we check for results to be transferred to the next sprint
// 3 then we go ahead and process the closing of the sprint

// PART 3 OF END SPRINT
// VALIDATE THE END DATE AS A NON CONFLICT BETWEEN OTHER SPRINTS
const endSprint = (
  selectedUnfinishedWorkPackages = [],
  currentSprint,
  closeModal = null
) => {
  const endDateValidation = (sprintArg) => {
    if (!validEndDate(sprintArg)) {
      adjustDates(
        sprintArg,
        CadenceStatuses.ACCEPTED,
        null,
        selectedUnfinishedWorkPackages,
        closeModal
      );
    } else {
      Store.dispatch(
        updateCadence(sprintArg.id, {
          status: CadenceStatuses.ACCEPTED,
          cadence: { end_date: sprintArg.end_date },
          unfinished_work_packages_ids: selectedUnfinishedWorkPackages
        })
      );
      if (closeModal) closeModal();
    }
  };
  endDateValidation(currentSprint);
};

// PART 2 OF ENDING SPRINTS
// check for any results that need to be transferred to the next sprint
export const checkResultsToBeTransferred = (currentSprint) => {
  // get all stories from store
  const stories = Store.getState().workPackageReducer.all || [];
  // filter stories from store to check for incomplete results
  const unfinishedResults = stories.filter(
    (w) =>
      w.cadence_ids.indexOf(currentSprint.id) !== -1 &&
      w.type === 'Result' &&
      w.state !== WorkPackageStatuses.ACCEPTED &&
      w.state !== WorkPackageStatuses.SHIPPABLE &&
      w.state !== WorkPackageStatuses.LIVE &&
      !w.deleted_at
  );

  // if there are any unfinished results, generate a modal to handle this / get confirmation of sending them to the next sprint
  if (unfinishedResults.length > 0) {
    Store.dispatch(
      openModal(modalTypes.unfinishedResults, {
        unfinishedResults,
        sprint: currentSprint,
        endGroup: endSprint,
        openDetailCard
      })
    );
  } else {
    // and if theres no incomplete items, go ahead and move to part 3 of completing the sprint
    endSprint([], currentSprint);
  }
};

// NEW END SPRINT STEP 1
export const validateEndDateOfSprint = (sprint) => {
  // get all sprints from store
  const sprints = Store.getState().cadenceReducer.cadences;
  const selectedTeamId = Store.getState().workPackageReducer.selectedSpace.id; // must be team space to end sprint

  // identify the sprint we are working on, pulling from the db if necessary / not provided
  const currentSprint =
    sprint ||
    sprints.find(
      (item) =>
        !item.deleted_at &&
        item.status === CadenceStatuses.IN_PROGRESS &&
        item.team_id === selectedTeamId
    );

  // Now handle any situation where the end date is not the planned end date
  const dateToday = formatDateHyphen(new Date());
  // if sprint date is not today
  if (dateToday !== currentSprint.end_date) {
    // determine if the sprint is ending early or late
    const endingEarly = moment(dateToday).isBefore(currentSprint.end_date);

    // define confirmation of changing date to today
    const actionChangeDateToToday = () => {
      currentSprint.end_date = dateToday;
      // proceed to the next step of ending a sprint
      checkResultsToBeTransferred(currentSprint);
    };

    // define no button as leaving end date as it is and proceed
    const actionLeaveDateAsIs = () =>
      // proceed to the next step of ending a sprint
      checkResultsToBeTransferred(currentSprint);

    let message = '';

    // define message to users for early ending sprints vs late ending sprints
    if (endingEarly) {
      message = `This sprint is scheduled to end on ${currentSprint.end_date}, do you want to end it now?
                   This might impact your team's velocity.`;
    } else {
      // ending late
      message = `This sprint was scheduled to end on ${currentSprint.end_date}.
      Would you like to change the end date to today?`;
    }

    // define actions on the modal based on early vs late ending
    let actions = {};
    let secondaryActions = {};
    // redefine close button to suit use case
    let closeButtonTitle = 'Cancel';

    if (endingEarly) {
      // set button actions for early ending sprints
      // If ending early we only offer to not end ( cancel ) or end it, no date modifications avail
      closeButtonTitle = `Don't end it`;
      actions = {
        Yes: {
          name: 'End it today',
          function: actionChangeDateToToday
        }
      };
    } else {
      // set button actions for late ending sprints
      // if ending late we ask whether to change date to today: no leaves it and is primary
      actions = {
        No: {
          name: `End it on ${currentSprint.end_date}`,
          function: actionLeaveDateAsIs
        }
      };
      // enable a secondary action without primary action emphasis
      secondaryActions = {
        Yes: {
          name: 'End it today',
          function: actionChangeDateToToday
        }
      };
    }
    // fire up modal with these config options
    Store.dispatch(
      openModal(modalTypes.message, {
        message,
        actions,
        id: 'message',
        closeButtonTitle,
        secondaryActions
      })
    );
  } else checkResultsToBeTransferred(currentSprint);
};
